import { type ComponentProps } from "react";

import { useLocaleCode } from "../lib/i18n.jsx";
import { aplicarTokens } from "../lib/runtime-config.js";

// Mapeia o markdown puro para as classes do design system e disponibiliza os
// componentes ricos no escopo de todo .mdx.
//
// A consequência prática: um .mdx escrito só com markdown já sai com a
// aparência certa. Quem edita a documentação não precisa saber que .doc-p ou
// .doc-table existem — e não tem como esquecer de aplicá-las.

import Anchor from "./Anchor.jsx";
import { Badge, Changelog, Downloads, Eyebrow, Lead, Step, Steps } from "./blocks.jsx";
import Callout from "./Callout.jsx";
import { Card, Cards } from "./Cards.jsx";
import CodeBlock from "./CodeBlock.jsx";
import Flow from "./Flow.jsx";
import Icon from "./Icon.jsx";
import Mermaid from "./Mermaid.jsx";
import MotorDemo from "./MotorDemo.jsx";
import MotorInit from "./MotorInit.jsx";
import { EnvironmentsNote, SwapCredentials } from "./Notes.jsx";
import Snippets from "./Snippets.jsx";
import { Tab, Tabs } from "./Tabs.jsx";

function InlineCode({ children, ...props }: ComponentProps<"code">) {
  const locale = useLocaleCode();
  return (
    <code className="doc-ic" {...props}>
      {typeof children === "string" ? aplicarTokens(children, locale) : children}
    </code>
  );
}

const components = {
  // ── Markdown → design system ──
  h1: (props: ComponentProps<"h1">) => <h1 className="doc-title" {...props} />,
  h2: (props: ComponentProps<"h2">) => <h2 className="doc-h2" {...props} />,
  h3: (props: ComponentProps<"h3">) => <h3 className="doc-h3" {...props} />,
  h4: (props: ComponentProps<"h4">) => <h4 className="doc-h4" {...props} />,
  p: (props: ComponentProps<"p">) => <p className="doc-p" {...props} />,
  ul: (props: ComponentProps<"ul">) => <ul className="doc-ul" {...props} />,
  ol: (props: ComponentProps<"ol">) => <ol className="doc-ol" {...props} />,
  a: Anchor,
  pre: CodeBlock,
  // Só o code inline chega aqui: o de dentro de ``` é consumido pelo CodeBlock,
  // que renderiza o HTML realçado direto e nunca monta este componente.
  // Também troca os tokens `{SANDBOX_*}` / `{SITE_URL}`: uma tabela de
  // credenciais escrita com tokens fica correta sem repetir o valor no .mdx.
  code: InlineCode,
  blockquote: (props: ComponentProps<"blockquote">) => <blockquote className="doc-quote" {...props} />,
  hr: () => <hr className="doc-hr" />,
  // GFM produz <table> nu; o wrapper dá o scroll horizontal no mobile.
  table: (props: ComponentProps<"table">) => (
    <div className="doc-table-wrap">
      <table className="doc-table" {...props} />
    </div>
  ),
  img: (props: ComponentProps<"img">) => <img className="doc-img" loading="lazy" {...props} />,

  // ── Componentes ricos, disponíveis sem import ──
  Callout,
  SwapCredentials,
  EnvironmentsNote,
  Snippets,
  Flow,
  Mermaid,
  MotorDemo,
  MotorInit,
  Icon,
  Cards,
  Card,
  Tabs,
  Tab,
  Lead,
  Eyebrow,
  Badge,
  Steps,
  Step,
  Downloads,
  Changelog,
};

export default components;
